import { useState } from "react";
import { MDBBadge, MDBBtn, MDBIcon } from "mdbreact";
import { preferredIdOf, supplierIdsOf, supplierLabel } from "./common";

/**
 * Suppliers of a brand with inline update: click to tag / untag the branch
 * suppliers, the star sets the preferred one (used when ordering).
 */
export default function SupplierCell({
  product,
  suppliers = [],
  supplierById = {},
  onSave,
}) {
  const [draft, setDraft] = useState(null),
    [isSaving, setIsSaving] = useState(false);

  const tagged = supplierIdsOf(product);
  const preferred = preferredIdOf(product);

  const startEdit = () => setDraft({ suppliers: tagged, supplier: preferred });

  // the first tagged supplier becomes preferred; untagging the preferred moves it
  const toggle = (id) =>
    setDraft((prev) => {
      const next = prev.suppliers.includes(id)
        ? prev.suppliers.filter((value) => value !== id)
        : [...prev.suppliers, id];
      return {
        suppliers: next,
        supplier: next.includes(prev.supplier) ? prev.supplier : next[0] || "",
      };
    });

  const setPreferred = (id) =>
    setDraft((prev) => ({
      suppliers: prev.suppliers.includes(id)
        ? prev.suppliers
        : [...prev.suppliers, id],
      supplier: id,
    }));

  const isDirty =
    !!draft &&
    (draft.supplier !== preferred ||
      draft.suppliers.length !== tagged.length ||
      draft.suppliers.some((id) => !tagged.includes(id)));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(product, {
        suppliers: draft.suppliers,
        // null clears it on update
        supplier: draft.supplier || null,
      });
      setDraft(null);
    } catch {
      // the error is notified by onSave; keep editing
    } finally {
      setIsSaving(false);
    }
  };

  if (!draft)
    return (
      <div
        role="button"
        tabIndex={0}
        className="d-flex flex-wrap align-items-center"
        style={{ cursor: "pointer", gap: 4 }}
        title="Click to update suppliers"
        onClick={startEdit}
        onKeyDown={(e) => e.key === "Enter" && startEdit()}
      >
        {!tagged.length && (
          <span className="text-muted small">No supplier</span>
        )}
        {tagged.map((id) => (
          <MDBBadge
            key={id}
            color={id === preferred ? "primary" : "light"}
            title={id === preferred ? "Preferred supplier" : ""}
          >
            {id === preferred && <MDBIcon icon="star" className="mr-1" />}
            {supplierLabel(supplierById[id])}
          </MDBBadge>
        ))}
        <MDBIcon icon="pen" className="text-muted small ml-1" />
      </div>
    );

  return (
    <div className="border rounded p-2 bg-white" style={{ minWidth: 220 }}>
      <div
        className="d-flex flex-wrap mb-2"
        style={{ gap: 4, maxHeight: 140, overflowY: "auto" }}
      >
        {!suppliers.length && (
          <small className="text-muted">
            No supplier yet. Add one in Sources &gt; Suppliers.
          </small>
        )}
        {suppliers.map((supplier) => {
          const isTagged = draft.suppliers.includes(supplier._id);
          const isPreferred = draft.supplier === supplier._id;
          return (
            <span
              key={supplier._id}
              className={`badge badge-pill d-inline-flex align-items-center py-1 px-2 ${
                isTagged ? "badge-primary" : "badge-light border"
              }`}
              style={{ cursor: "pointer" }}
              title={isTagged ? "Click to untag" : "Click to tag"}
              onClick={() => toggle(supplier._id)}
            >
              <MDBIcon icon={isTagged ? "check" : "plus"} className="mr-1" />
              {supplierLabel(supplier)}
              {isTagged && (
                <MDBIcon
                  icon="star"
                  far={!isPreferred}
                  className={`ml-1 ${isPreferred ? "text-warning" : ""}`}
                  title="Set as preferred supplier"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreferred(supplier._id);
                  }}
                />
              )}
            </span>
          );
        })}
      </div>
      <div className="d-flex justify-content-end" style={{ gap: 4 }}>
        <MDBBtn
          size="sm"
          color="light"
          className="m-0 px-2 py-1"
          disabled={isSaving}
          onClick={() => setDraft(null)}
        >
          Cancel
        </MDBBtn>
        <MDBBtn
          size="sm"
          color="primary"
          className="m-0 px-2 py-1"
          disabled={isSaving || !isDirty}
          onClick={handleSave}
        >
          <MDBIcon
            icon={isSaving ? "spinner" : "check"}
            spin={isSaving}
            className="mr-1"
          />
          Save
        </MDBBtn>
      </div>
    </div>
  );
}
